import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import CourseCard from "../components/CourseCard";
import { useI18n } from "../contexts/I18nContext";
import { Search } from "lucide-react";

export default function Courses() {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { t, lang } = useI18n();

  const category = params.get("category") || "";
  const level = params.get("level") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  // Per-page SEO
  useEffect(() => {
    const cat = category ? ` — ${category.charAt(0).toUpperCase() + category.slice(1)}` : "";
    document.title = `All Courses${cat} | EDUHUB Academy`;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://eduhubcenter.com" },
        { "@type": "ListItem", "position": 2, "name": "Courses", "item": "https://eduhubcenter.com/courses" },
        ...(category ? [{ "@type": "ListItem", "position": 3, "name": category.charAt(0).toUpperCase() + category.slice(1), "item": `https://eduhubcenter.com/courses?category=${category}` }] : []),
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "breadcrumb-schema";
    el.text = JSON.stringify(breadcrumb);
    document.head.appendChild(el);
    return () => { document.title = "EDUHUB — A Classical Academy for All Ages"; const s = document.getElementById("breadcrumb-schema"); if (s) s.remove(); };
  }, [category]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (level) q.level = level;
    api
      .get("/courses", { params: q })
      .then((r) => setCourses(r.data || []))
      .finally(() => setLoading(false));
  }, [category, level]);

  const filtered = courses.filter((c) => {
    const title = (lang === "ro" && c.title_ro) || c.title;
    return title.toLowerCase().includes(search.toLowerCase());
  });

  const updateParam = (key, val) => {
    const np = new URLSearchParams(params);
    if (val) np.set(key, val);
    else np.delete(key);
    setParams(np);
  };

  return (
    <div data-testid="courses-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-10 border-b border-eduhu-border">
        <div className="overline text-eduhu-claret mb-3">{t("courses.eyebrow")}</div>
        <h1 className="font-serif text-5xl lg:text-7xl tracking-tight text-eduhu-dark">
          {t("courses.title")}
        </h1>
        <p className="mt-4 text-lg text-eduhu-inkSoft max-w-2xl">
          {t("courses.subtitle")}
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 space-y-8 order-2 lg:order-1" data-testid="courses-filters">
          <div>
            <div className="overline text-eduhu-inkSoft mb-3">Search</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-eduhu-inkSoft" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="w-full bg-eduhu-bg2 border border-eduhu-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-eduhu-dark"
              />
            </div>
          </div>

          <div>
            <div className="overline text-eduhu-inkSoft mb-3">Discipline</div>
            <ul className="space-y-1.5 text-sm">
              <li>
                <button
                  data-testid="cat-filter-all"
                  onClick={() => updateParam("category", "")}
                  className={`text-left nav-link ${!category ? "text-eduhu-gold" : ""}`}
                >
                  {t("common.allCategories")}
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`cat-filter-${c.id}`}
                    onClick={() => updateParam("category", c.id)}
                    className={`text-left nav-link ${category === c.id ? "text-eduhu-gold" : ""}`}
                  >
                    {lang === "ro" && c.name_ro ? c.name_ro : c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="overline text-eduhu-inkSoft mb-3">Level</div>
            <ul className="space-y-1.5 text-sm">
              {[
                { v: "", l: t("common.allLevels") },
                { v: "beginner", l: t("course.beginner") },
                { v: "intermediate", l: t("course.intermediate") },
                { v: "advanced", l: t("course.advanced") },
              ].map((opt) => (
                <li key={opt.v || "all"}>
                  <button
                    data-testid={`level-filter-${opt.v || "all"}`}
                    onClick={() => updateParam("level", opt.v)}
                    className={`text-left nav-link ${level === opt.v ? "text-eduhu-gold" : ""}`}
                  >
                    {opt.l}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9 order-1 lg:order-2">
          {loading ? (
            <div className="text-eduhu-inkSoft py-20 text-center" data-testid="courses-loading">
              {t("common.loading")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-eduhu-inkSoft py-20 text-center" data-testid="courses-empty">
              No courses match.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-eduhu-border border border-eduhu-border">
              {filtered.map((c) => (
                <CourseCard key={c.id} course={c} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
